import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchDevices, fetchDevicesCount, deleteDevices } from '../api';
import type { DeviceQueryParams, DeviceCountParams } from '../types';

// Query keys are scoped by companyId because the same paging/search params
// return different devices per company.
export const devicesKeys = {
  all: ['devices'] as const,
  lists: () => [...devicesKeys.all, 'list'] as const,
  // Prefixes for invalidating every list/count of one company, whatever its paging params are
  listsOfCompany: (companyId: string) => [...devicesKeys.lists(), companyId] as const,
  list: (companyId: string, params: DeviceQueryParams) =>
    [...devicesKeys.listsOfCompany(companyId), params] as const,
  counts: () => [...devicesKeys.all, 'count'] as const,
  countsOfCompany: (companyId: string) => [...devicesKeys.counts(), companyId] as const,
  count: (companyId: string, params: DeviceCountParams) =>
    [...devicesKeys.countsOfCompany(companyId), params] as const,
};

export function useDevices(companyId: string, params: DeviceQueryParams) {
  return useQuery({
    queryKey: devicesKeys.list(companyId, params),
    queryFn: () => fetchDevices(companyId, params),
    enabled: !!companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useDevicesCount(
  companyId: string,
  params: DeviceCountParams,
  pageSize: number
) {
  return useQuery({
    queryKey: devicesKeys.count(companyId, params),
    queryFn: () => fetchDevicesCount(companyId, params, pageSize),
    enabled: !!companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useDeleteDevices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceIds: string[]) => deleteDevices(deviceIds),
    onSuccess: () => {
      // Invalidate both device list and count queries for more precise cache management
      queryClient.invalidateQueries({ queryKey: devicesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: devicesKeys.counts() });
    },
  });
}
